import { useEffect, useRef, useState, useCallback } from "react";

function GalleryLightbox({ images, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const touchStartRef = useRef(null);

  const navigate = useCallback((dir) => {
    setIndex((i) => (i + dir + images.length) % images.length);
  }, [images.length]);

  // Lock scroll — block wheel + touchmove at document level (non-passive)
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("wheel", prevent, { passive: false });
    document.addEventListener("touchmove", prevent, { passive: false });
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      document.removeEventListener("wheel", prevent);
      document.removeEventListener("touchmove", prevent);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") navigate(1);
      if (e.key === "ArrowLeft") navigate(-1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, onClose]);

  // Touch swipe
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartRef.current === null) return;
    const diff = touchStartRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) navigate(diff > 0 ? 1 : -1);
    touchStartRef.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col select-none bg-black/80 backdrop-blur-md"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
        <span className="font-sans text-sm text-white/40">
          {index + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white text-xl leading-none hover:bg-white/20 hover:border-white/40 transition-colors"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Image area — click backdrop to close */}
      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden px-4 md:px-16"
        onClick={onClose}
      >
        {/* Prev */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            className="hidden md:flex absolute left-4 z-10 w-10 h-10 rounded-full bg-white/10 border border-white/20 items-center justify-center text-white/70 hover:text-white hover:bg-white/20 hover:border-white/40 transition-colors"
            aria-label="Previous"
          >
            ←
          </button>
        )}

        <img
          src={images[index]}
          alt={`View ${index + 1}`}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          className="block rounded-lg shadow-2xl"
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            userSelect: "none",
          }}
        />

        {/* Next */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(1); }}
            className="hidden md:flex absolute right-4 z-10 w-10 h-10 rounded-full bg-white/10 border border-white/20 items-center justify-center text-white/70 hover:text-white hover:bg-white/20 hover:border-white/40 transition-colors"
            aria-label="Next"
          >
            →
          </button>
        )}

        {/* Mobile swipe hint */}
        {images.length > 1 && (
          <p className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 font-sans text-xs text-white/30 pointer-events-none">
            Swipe to navigate
          </p>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex-shrink-0 px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2 justify-center overflow-x-auto pb-1">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`flex-shrink-0 w-12 h-9 md:w-14 md:h-10 rounded-lg overflow-hidden border-2 transition-all ${
                  i === index
                    ? "border-white opacity-100"
                    : "border-white/20 opacity-50 hover:opacity-80 hover:border-white/40"
                }`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryLightbox;
